import type { F0SelectItemObject, F0SelectItemProps } from '@factorialco/f0-react'
import { Money, Pencil, Timer } from '@factorialco/f0-react/icons/app'
import type {
  ContractsValueObjectsAdditionalCompensationRecurrenceEnum,
  PayrollConceptSelectFieldFragment,
} from 'generated/resources/graphql'

import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import type { PayrollConceptWithCategory } from 'modules/compensations/components/PayrollConceptSelectField/CategorizedPayrollConceptUtil'
import {
  mapAmountAndUpperLimitFromCents,
  mapAmountAndUpperLimitToCents,
} from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary/_AdditionalCompensations/AdditionalCompensationForm/AdditionalCompensationFormUtils'
import type { InMemoryAdditionalCompensation } from 'modules/contracts/lib/contractFlowContext'
import { parseDateString } from 'modules/contracts/lib/utils'

import {
  type AdditionalCompensationWizardAmountStrategyType,
  type AdditionalCompensationWizardInitialValues,
  type BasicStepValues,
  type DefinitionStepValues,
  type NormalizableDefinitionStepValues,
  AMOUNT_STRATEGY_TYPE_VALUES,
  type RawDefinitionStepValues,
  FIXED_AMOUNT_STRATEGY,
  PER_WORKED_DAY_CALCULATION_SOURCE,
  PER_WORKED_DAY_REFERENCE_TIME,
  PER_WORKED_DAY_STRATEGY,
  RECURRENCE_VALUES,
  VARIABLE_AMOUNT_STRATEGY,
  getDefaultPerWorkedDayFields,
  normalizeDefinitionNumber,
  withPerWorkedDayDefinitionDefaults,
} from './additionalCompensationWizard.shared'

type DefinitionSectionCopy = {
  title: string
  description?: string
}

type AmountStrategyOptionConfig = Pick<
  F0SelectItemObject<AdditionalCompensationWizardAmountStrategyType>,
  'label' | 'description' | 'icon'
>

const getDurationSecondsFromMinutes = (minutes: number | null | undefined) =>
  minutes == null ? null : minutes * 60

const getRoundedMinutesFromHours = (hours: number | null | undefined) =>
  hours == null ? null : Math.round(hours * 60)

const normalizeDefinitionValues = (
  definitionValues: NormalizableDefinitionStepValues
): DefinitionStepValues => {
  const fixedAmount = normalizeDefinitionNumber(definitionValues.fixedAmount)
  const maximumAmountPerCycle = normalizeDefinitionNumber(definitionValues.maximumAmountPerCycle)
  const amountPerDay = normalizeDefinitionNumber(definitionValues.perWorkedDayAmountPerDay)
  const minimumHoursPerDaySeconds = normalizeDefinitionNumber(
    definitionValues.perWorkedDayMinimumHoursPerDay
  )
  const minimumHoursPerDay =
    minimumHoursPerDaySeconds == null ? null : minimumHoursPerDaySeconds / 3600
  const hasPerWorkedDayValues =
    definitionValues.perWorkedDayAmountPerDay !== undefined ||
    definitionValues.perWorkedDayDaysCountedFrom !== undefined ||
    definitionValues.perWorkedDayMinimumHoursPerDay !== undefined ||
    definitionValues.perWorkedDayWorkLocations !== undefined ||
    definitionValues.perWorkedDayIncludedAbsenceLeaveTypeIds !== undefined ||
    definitionValues.perWorkedDayEligibleDays !== undefined

  return {
    fixedAmount: fixedAmount == null ? null : fixedAmount,
    maximumAmountPerCycle: maximumAmountPerCycle == null ? null : maximumAmountPerCycle,
    perWorkedDayFields: hasPerWorkedDayValues
      ? {
          daysCountedFrom: definitionValues.perWorkedDayDaysCountedFrom,
          amountPerDay: amountPerDay == null ? null : amountPerDay,
          minimumHoursPerDay,
          workLocations: definitionValues.perWorkedDayWorkLocations,
          includedAbsenceLeaveTypeIds: definitionValues.perWorkedDayIncludedAbsenceLeaveTypeIds,
          eligibleDays: definitionValues.perWorkedDayEligibleDays,
        }
      : undefined,
  }
}

const getDefinitionValuesFromAdditionalCompensation = (
  additionalCompensation: InMemoryAdditionalCompensation
): Partial<RawDefinitionStepValues> => {
  const normalizedAdditionalCompensation = mapAmountAndUpperLimitFromCents(additionalCompensation)
  const perWorkedDayDefinition = normalizedAdditionalCompensation.perWorkedDayDefinition

  return {
    fixedAmount: normalizedAdditionalCompensation.amountStrategy?.amount,
    maximumAmountPerCycle:
      normalizedAdditionalCompensation.amountStrategy &&
      'upperLimit' in normalizedAdditionalCompensation.amountStrategy
        ? normalizedAdditionalCompensation.amountStrategy.upperLimit
        : undefined,
    perWorkedDayAmountPerDay:
      normalizedAdditionalCompensation.amountStrategyType === PER_WORKED_DAY_STRATEGY
        ? normalizedAdditionalCompensation.amountStrategy?.amount
        : undefined,
    perWorkedDayDaysCountedFrom:
      normalizedAdditionalCompensation.amountStrategyType === PER_WORKED_DAY_STRATEGY
        ? getDefaultPerWorkedDayFields().daysCountedFrom
        : undefined,
    perWorkedDayMinimumHoursPerDay:
      normalizedAdditionalCompensation.amountStrategyType === PER_WORKED_DAY_STRATEGY
        ? getDurationSecondsFromMinutes(perWorkedDayDefinition?.minimumAmountOfHoursInMinutes)
        : undefined,
    perWorkedDayWorkLocations:
      normalizedAdditionalCompensation.amountStrategyType === PER_WORKED_DAY_STRATEGY
        ? (perWorkedDayDefinition?.workLocations ?? [
            ...getDefaultPerWorkedDayFields().workLocations,
          ])
        : undefined,
    perWorkedDayIncludedAbsenceLeaveTypeIds:
      normalizedAdditionalCompensation.amountStrategyType === PER_WORKED_DAY_STRATEGY
        ? (perWorkedDayDefinition?.timeoffLeaveTypeIds ?? [])
        : undefined,
    perWorkedDayEligibleDays:
      normalizedAdditionalCompensation.amountStrategyType === PER_WORKED_DAY_STRATEGY
        ? (perWorkedDayDefinition?.eligibleDays ?? [])
        : undefined,
  }
}

const getSelectedPayrollConcept = ({
  payrollConcepts,
  payrollConceptId,
}: {
  payrollConcepts: PayrollConceptSelectFieldFragment[]
  payrollConceptId?: string
}) => payrollConcepts.find((payrollConcept) => payrollConcept.id === payrollConceptId)

const buildAmountStrategy = ({
  amountStrategyType,
  definitionValues,
}: {
  amountStrategyType: AdditionalCompensationWizardAmountStrategyType
  definitionValues: DefinitionStepValues
}) => {
  switch (amountStrategyType) {
    case FIXED_AMOUNT_STRATEGY:
      return {
        __typename: 'ContractsFixedAmountStrategy' as const,
        amount: definitionValues.fixedAmount ?? null,
      }
    case VARIABLE_AMOUNT_STRATEGY:
      return {
        __typename: 'ContractsVariableAmountStrategy' as const,
        amount: null,
        upperLimit: definitionValues.maximumAmountPerCycle ?? null,
      }
    case PER_WORKED_DAY_STRATEGY:
      return {
        __typename: 'ContractsFixedAmountStrategy' as const,
        amount: definitionValues.perWorkedDayFields?.amountPerDay ?? null,
      }
    default:
      return {
        __typename: 'ContractsFixedAmountStrategy' as const,
        amount: null,
      }
  }
}

const buildPerWorkedDayDefinition = ({
  amountStrategyType,
  definitionValues,
  companyId,
}: {
  amountStrategyType: AdditionalCompensationWizardAmountStrategyType
  definitionValues: DefinitionStepValues
  companyId: string
}) => {
  if (amountStrategyType !== PER_WORKED_DAY_STRATEGY) {
    return null
  }

  return {
    calculationSource: PER_WORKED_DAY_CALCULATION_SOURCE,
    companyId,
    referenceTime: PER_WORKED_DAY_REFERENCE_TIME,
    minimumAmountOfHoursInMinutes: getRoundedMinutesFromHours(
      definitionValues.perWorkedDayFields?.minimumHoursPerDay
    ),
    workLocations: definitionValues.perWorkedDayFields?.workLocations ?? [],
    timeoffLeaveTypeIds: definitionValues.perWorkedDayFields?.includedAbsenceLeaveTypeIds ?? [],
    eligibleDays: definitionValues.perWorkedDayFields?.eligibleDays ?? [],
  }
}

const buildAdditionalCompensation = ({
  basicValues,
  definitionValues,
  companyId,
  contractVersionId,
  payrollConcepts,
  createTemporaryId,
  additionalCompensation,
}: {
  basicValues: BasicStepValues
  definitionValues: DefinitionStepValues
  companyId: string
  contractVersionId: string
  payrollConcepts: PayrollConceptSelectFieldFragment[]
  createTemporaryId: () => string
  additionalCompensation?: InMemoryAdditionalCompensation
}): InMemoryAdditionalCompensation => {
  const selectedPayrollConcept = getSelectedPayrollConcept({
    payrollConcepts,
    payrollConceptId: basicValues.payrollConceptId,
  })

  const amountStrategy = buildAmountStrategy({
    amountStrategyType: basicValues.amountStrategyType,
    definitionValues,
  })

  const perWorkedDayDefinition = buildPerWorkedDayDefinition({
    amountStrategyType: basicValues.amountStrategyType,
    definitionValues,
    companyId,
  })

  const payrollConcept = selectedPayrollConcept
    ? {
        __typename: 'PayrollConcept' as const,
        id: selectedPayrollConcept.id,
        label: selectedPayrollConcept.label,
        name: selectedPayrollConcept.name,
        unitType: selectedPayrollConcept.unitType,
      }
    : (additionalCompensation?.payrollConcept ?? null)

  const baseAdditionalCompensation: Omit<InMemoryAdditionalCompensation, 'id' | 'tmpID'> = {
    __typename: 'ContractsAdditionalCompensation',
    contractVersionId: additionalCompensation?.contractVersionId ?? contractVersionId,
    description: additionalCompensation?.description ?? null,
    amountStrategyType: basicValues.amountStrategyType,
    amountStrategy,
    perWorkedDayDefinition,
    payrollConceptId: basicValues.payrollConceptId,
    payrollConcept,
    recurrence: basicValues.recurrence,
    firstPaymentOn: i18nDateToString(basicValues.firstPaymentOn, 'backend'),
  }

  if (!additionalCompensation) {
    return {
      ...baseAdditionalCompensation,
      tmpID: createTemporaryId(),
    }
  }

  if (additionalCompensation.id && additionalCompensation.tmpID) {
    return {
      ...baseAdditionalCompensation,
      id: additionalCompensation.id,
      tmpID: additionalCompensation.tmpID,
    }
  }

  if (additionalCompensation.id) {
    return {
      ...baseAdditionalCompensation,
      id: additionalCompensation.id,
    }
  }

  return {
    ...baseAdditionalCompensation,
    tmpID: additionalCompensation.tmpID ?? createTemporaryId(),
  }
}

const getDefinitionSectionCopy = (
  amountStrategyType?: AdditionalCompensationWizardAmountStrategyType
): DefinitionSectionCopy => {
  switch (amountStrategyType) {
    case FIXED_AMOUNT_STRATEGY:
      return {
        title: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.fixed_amount.title'
        ),
        description: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.fixed_amount.description'
        ),
      }
    case VARIABLE_AMOUNT_STRATEGY:
      return {
        title: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.variable_amount.title'
        ),
        description: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.variable_amount.description'
        ),
      }
    case PER_WORKED_DAY_STRATEGY:
      return {
        title: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.title'
        ),
        description: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.description'
        ),
      }
    default:
      return {
        title: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.title'
        ),
      }
  }
}

const getRecurrenceOptionLabel = (
  recurrence: ContractsValueObjectsAdditionalCompensationRecurrenceEnum
) => {
  switch (recurrence) {
    case 'monthly':
      return i18n.t(
        'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.monthly_option'
      )
    case 'every_2_months':
      return i18n.t(
        'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_2_months_option'
      )
    case 'every_3_months':
      return i18n.t(
        'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_3_months_option'
      )
    case 'every_4_months':
      return i18n.t(
        'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_4_months_option'
      )
    case 'every_5_months':
      return i18n.t(
        'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_5_months_option'
      )
    case 'every_6_months':
      return i18n.t(
        'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_6_months_option'
      )
    case 'every_7_months':
      return i18n.t(
        'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_7_months_option'
      )
    case 'every_8_months':
      return i18n.t(
        'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_8_months_option'
      )
    case 'every_9_months':
      return i18n.t(
        'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_9_months_option'
      )
    case 'every_10_months':
      return i18n.t(
        'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_10_months_option'
      )
    case 'every_11_months':
      return i18n.t(
        'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_11_months_option'
      )
    case 'every_12_months':
      return i18n.t(
        'contract_versions.additional_compensation.additional_compensation_form_fields.recurrence.every_12_months_option'
      )
    default:
      return recurrence
  }
}

const getAmountStrategyOptionConfig = (
  amountStrategyType: AdditionalCompensationWizardAmountStrategyType
): AmountStrategyOptionConfig => {
  switch (amountStrategyType) {
    case FIXED_AMOUNT_STRATEGY:
      return {
        label: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.fields.calculation_method.options.fixed_amount.label'
        ),
        description: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.fields.calculation_method.options.fixed_amount.description'
        ),
        icon: Money,
      }
    case VARIABLE_AMOUNT_STRATEGY:
      return {
        label: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.fields.calculation_method.options.manual_entry_each_cycle.label'
        ),
        description: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.fields.calculation_method.options.manual_entry_each_cycle.description'
        ),
        icon: Pencil,
      }
    case PER_WORKED_DAY_STRATEGY:
      return {
        label: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.fields.calculation_method.options.per_worked_day.label'
        ),
        description: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.fields.calculation_method.options.per_worked_day.description'
        ),
        icon: Timer,
      }
  }

  return {
    label: amountStrategyType,
    description: amountStrategyType,
    icon: Money,
  }
}

const buildRecurrenceOptions =
  (): F0SelectItemProps<ContractsValueObjectsAdditionalCompensationRecurrenceEnum>[] =>
    RECURRENCE_VALUES.map((recurrence) => ({
      label: getRecurrenceOptionLabel(recurrence),
      value: recurrence,
    }))

const buildAmountStrategyTypeOptions = (): Array<
  F0SelectItemProps<AdditionalCompensationWizardAmountStrategyType>
> =>
  AMOUNT_STRATEGY_TYPE_VALUES.map((amountStrategyType) => ({
    value: amountStrategyType,
    ...getAmountStrategyOptionConfig(amountStrategyType),
  }))

const shouldShowCompensationExcludedFromFirstPeriodCallout = ({
  firstPaymentOn,
  effectiveOn,
}: {
  firstPaymentOn: unknown
  effectiveOn?: string
}) => {
  if (!(firstPaymentOn instanceof Date) || !effectiveOn) return false

  const effectiveDate = parseDateString(effectiveOn)
  if (!effectiveDate?.isValid()) return false

  const isNotFirstDay = effectiveDate.date() !== 1
  if (!isNotFirstDay) return false

  return (
    firstPaymentOn.getFullYear() === effectiveDate.year() &&
    firstPaymentOn.getMonth() === effectiveDate.month()
  )
}

const createBasicContextFromValues = (values: BasicStepValues) => ({
  amountStrategyType: values.amountStrategyType,
  payrollConceptId: values.payrollConceptId,
})

const buildWizardInitialValues = ({
  additionalCompensation,
  defaultFirstPaymentDate,
}: {
  additionalCompensation?: InMemoryAdditionalCompensation
  defaultFirstPaymentDate?: Date
}): AdditionalCompensationWizardInitialValues | undefined => {
  if (!additionalCompensation) {
    return undefined
  }

  const parsedFirstPaymentOn = additionalCompensation.firstPaymentOn
    ? parseDateString(additionalCompensation.firstPaymentOn)
    : null
  const definitionValues = getDefinitionValuesFromAdditionalCompensation(additionalCompensation)
  const amountStrategyType = additionalCompensation.amountStrategyType || FIXED_AMOUNT_STRATEGY

  return {
    basic: {
      payrollConceptId: additionalCompensation.payrollConceptId,
      amountStrategyType,
      recurrence:
        amountStrategyType === PER_WORKED_DAY_STRATEGY
          ? 'monthly'
          : (RECURRENCE_VALUES.find(
              (recurrence) => recurrence === additionalCompensation.recurrence
            ) ?? 'monthly'),
      firstPaymentOn: parsedFirstPaymentOn?.isValid()
        ? parsedFirstPaymentOn.toDate()
        : defaultFirstPaymentDate,
    },
    definition:
      amountStrategyType === PER_WORKED_DAY_STRATEGY
        ? withPerWorkedDayDefinitionDefaults(definitionValues)
        : definitionValues,
  }
}

const buildMappedAdditionalCompensation = (
  params: Parameters<typeof buildAdditionalCompensation>[0]
) => mapAmountAndUpperLimitToCents(buildAdditionalCompensation(params))

export {
  buildAdditionalCompensation,
  buildAmountStrategyTypeOptions,
  buildMappedAdditionalCompensation,
  buildRecurrenceOptions,
  buildWizardInitialValues,
  createBasicContextFromValues,
  getDefinitionValuesFromAdditionalCompensation,
  getDefinitionSectionCopy,
  getSelectedPayrollConcept,
  normalizeDefinitionValues,
  shouldShowCompensationExcludedFromFirstPeriodCallout,
}

export type { PayrollConceptWithCategory }
